import { useEffect } from 'react'
import { RouterProvider } from '@tanstack/react-router'
import { router } from './router'
import { useMessage } from './hooks/useMessage'
import { useCompletionNotification } from './hooks/useCompletionNotification'
import { meAPI } from './services/api'
import { useConversations } from './hooks/useConversations'
import { useConversationStore } from './stores/conversationStore'
import { useUserStore } from './stores/userStore'
import { useAuth } from './hooks/useAuth'
import { Toaster } from './components/ui/toaster'
import { Toaster as SonnerToaster } from 'sonner'
import { logger } from './utils/logger'
import { conversationUpdatesService } from './services/conversationUpdatesService'
import { workerSecurityService } from './services/workerSecurityService'
import { realtimeStreamManager } from './services/realtimeStreamManager'
import { ImpersonationBanner } from './components/admin/ImpersonationBanner'
import { TermsGate } from './components/legal/TermsGate'
import { useImpersonationStore } from './stores/impersonationStore'
import { resetUserScopedClientState, shouldResetForUidChange, LAST_AUTH_UID_KEY } from './utils/sessionReset'

function App() {
  const { error: messageError } = useMessage()

  // Enable notification sounds when AI responds (while tab is not focused)
  useCompletionNotification()
  const { setConversations, setLoading, setError, updateConversation } = useConversationStore()
  const { activeTeam, setActiveTeam, setActiveOrganization, setTeams, setFeatures, termsAccepted, setTermsAccepted } = useUserStore()
  const { user, isAuthenticated, loading: authLoading, emailVerified, isApproved } = useAuth()
  const { isImpersonating, impersonatedUser } = useImpersonationStore()
  const viewingAsId = isImpersonating ? impersonatedUser?.id : undefined

  // Account-switch guard (backup for a logout that didn't run our cleanup —
  // token expiry, a switch in another tab). If the authenticated uid differs
  // from the last one we saw, purge the previous account's cached projects/team
  // before they render. Runs before the /me bootstrap below.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.uid) return
    const lastUid = localStorage.getItem(LAST_AUTH_UID_KEY)
    if (shouldResetForUidChange(lastUid, user.uid)) {
      resetUserScopedClientState()
    }
    try { localStorage.setItem(LAST_AUTH_UID_KEY, user.uid) } catch { /* ignore */ }
  }, [user?.uid, isAuthenticated, authLoading])

  // Bootstrap the active team/org from the server (the SSE stream 403s on a team
  // the user isn't a member of, so we must use a real membership, not a default).
  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    let cancelled = false
    meAPI.get()
      .then((me) => {
        if (cancelled) return
        setTeams(me.teams.map((t) => ({ id: t.id, name: t.name, organizationId: t.organizationId })))
        setFeatures(me.features ?? [])
        // Clickwrap consent: the blocking gate shows until the user accepts the current
        // Terms version (a front-door build waits on it too). Never gate an admin "viewing as".
        setTermsAccepted(isImpersonating || me.user?.needsTermsAcceptance !== true)
        // Fall back to the first team membership when /me omits an explicit
        // activeTeam, so the team-gated conversation query fires immediately.
        // The query is gated on activeTeam.id, which avoids a premature getAll().
        const team = me.activeTeam ?? me.teams?.[0]
        if (team) {
          setActiveTeam({ id: team.id, name: team.name, organizationId: team.organizationId })
        }
        if (me.activeOrganization) {
          setActiveOrganization(me.activeOrganization)
        }
        // Website analytics (lazy chunk, fire-and-forget): link the landing-site
        // visitor to this account and record a brand-new sign-up. Never while an
        // admin is "viewing as" someone — that would credit the admin's visit to them.
        if (!isImpersonating && me.user?.id) {
          import('./analytics/postAuth')
            .then(({ runPostAuthAnalytics }) =>
              runPostAuthAnalytics({ userId: me.user.id, serverCreatedAt: me.user.createdAt }),
            )
            .catch(() => { /* analytics never disrupts the app */ })
        }
      })
      .catch((err) => logger.error('Failed to bootstrap team context from /me:', err))
    return () => { cancelled = true }
    // Re-fetch on impersonation toggle: the api client sends X-Impersonate-User,
    // so /me then returns the impersonated user's teams + features (admin "View As").
  }, [isAuthenticated, authLoading, isImpersonating, setActiveTeam, setActiveOrganization, setTeams, setFeatures, setTermsAccepted])


  // Check worker assignment before loading anything
  const isChecking = false;

  // Conversations are fetched via react-query (team-keyed, cached, deduped across
  // every consumer). The Zustand store stays the read-model for the rest of the
  // app, hydrated from the query result below.
  const conversationsQuery = useConversations(
    activeTeam?.id ?? null,
    isAuthenticated && !authLoading && !!activeTeam?.id,
  )

  useEffect(() => {
    if (!conversationsQuery.data) return
    setConversations(conversationsQuery.data.conversations)
    // Recover incomplete messages once conversations are populated.
    setTimeout(() => {
      realtimeStreamManager.recoverIncompleteMessages()
    }, 500)
  }, [conversationsQuery.data, setConversations])

  useEffect(() => {
    setLoading(conversationsQuery.isLoading)
  }, [conversationsQuery.isLoading, setLoading])

  useEffect(() => {
    const err = conversationsQuery.error
    if (err) {
      logger.error('Failed to load conversations:', err)
      if (err instanceof Error && !err.message.includes('401')) {
        setError(err.message)
      }
    } else {
      setError(null)
    }
  }, [conversationsQuery.error, setError])

  // Set up real-time conversation updates listener (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated || !activeTeam?.id) {
      return
    }

    // Start listening for conversation updates
    conversationUpdatesService.startListening(activeTeam.id)

    // Set up callback for conversation updates
    const unsubscribe = conversationUpdatesService.onConversationUpdate((conversationId, updates) => {
      logger.info('📝 Received conversation update:', conversationId, updates)

      // Update the conversation in store with new metadata
      updateConversation(conversationId, {
        metadata: updates
      })
    })

    // Cleanup on unmount or dependency change
    return () => {
      unsubscribe()
      conversationUpdatesService.stopListening()
    }
    // viewingAsId: View As changes whose stream it is (the server opens the viewed user's), even on the same team.
  }, [activeTeam?.id, isAuthenticated, viewingAsId, updateConversation])

  // Show message service status in development
  if (import.meta.env.DEV) {
    logger.debug('Message Service Status:', { messageError })
  }

  // Additional security check - if service detected unauthorized, block everything
  // Skip for /agent-chat which handles its own auth via ivy-chat session tokens
  if (workerSecurityService.isCurrentlyUnauthorized() && !window.location.pathname.startsWith('/agent-chat')) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl mb-4">Access Denied</h1>
          <p>Redirecting to login...</p>
        </div>
      </div>
    )
  }

  // Show unauthorized screen if worker mismatch detected
  // if (isUnauthorized) {
  //   return (
  //     <UnauthorizedWorker
  //       assignedUser={assignmentInfo.assignedTo}
  //       currentUser={assignmentInfo.currentUser}
  //     />
  //   )
  // }

  // Show loading while checking worker assignment
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating worker access...</p>
        </div>
      </div>
    )
  }

  // Never show the consent gate over the legal pages themselves — the user opens
  // Terms/Privacy/Refund from the gate to read them before accepting, so the
  // overlay must not cover them. (The gate links open these in a new tab.)
  const onLegalPage = /^\/(terms|privacy|refund)\b/.test(window.location.pathname)

  return (
    <div className="h-full w-full bg-background overflow-hidden">
      {/* Impersonation Banner - shows when admin is viewing as another user */}
      <ImpersonationBanner />

      {/* Message Error Bar (only in development) */}
      {import.meta.env.DEV && messageError && (
        <div className="px-4 py-2 text-sm text-center bg-destructive/10 text-destructive border-b">
          🔴 Message Error: {messageError}
        </div>
      )}

      <div className={`${import.meta.env.DEV && messageError ? 'h-[calc(100%-2.5rem)]' : 'h-full'} overflow-hidden`}>
        <RouterProvider router={router} />
      </div>
      {/* Only after email is verified + account approved, so it never stacks over
          the verify/waitlist screens — and never over the legal pages the user
          opens to read before accepting. */}
      <TermsGate open={termsAccepted === false && emailVerified && isApproved && !onLegalPage} onAccepted={() => setTermsAccepted(true)} />
      <Toaster />
      <SonnerToaster richColors position="bottom-right" />
    </div>
  )
}

export default App